import { Form, Upload } from 'antd'
import type { FormInstance } from 'antd'
import { Upload as UploadIcon, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

export interface VersionFormValues {
  id?: string
  name: string
  apkFile?: any
  manualFile?: any
  description?: string
}

interface VersionFormProps {
  form: FormInstance<VersionFormValues>
  mode: 'create' | 'update'
  onSubmit: (values: VersionFormValues) => void
}

const PILL_INPUT =
  '!bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'
const TEXTAREA_INPUT =
  'rounded-2xl py-3 !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export function VersionForm({ form, mode, onSubmit }: VersionFormProps) {  
  const apkFileList = Form.useWatch('apkFile', form)
  const manualFileList = Form.useWatch('manualFile', form)

  const uploadedApkFile =
    apkFileList && apkFileList.length > 0 ? apkFileList[0] : null
  const uploadedManualFile =
    manualFileList && manualFileList.length > 0 ? manualFileList[0] : null

  const handleCustomUpload = async (
    { file: _file, onSuccess, onProgress }: any,
    _folderName: string,
  ) => {
    setTimeout(() => {
      onProgress({ percent: 100 })
      onSuccess({ id: 'local-file' })
    }, 100)
  }

  const handleFinish = (values: VersionFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as VersionFormValues

    onSubmit(trimmedValues)
  }

  const handlePreview = (e: React.MouseEvent, file: any) => {
    e.stopPropagation()
    e.preventDefault()

    let url = file.url || file.preview
    if (!url && file.originFileObj) {
      url = URL.createObjectURL(file.originFileObj)
    }

    if (url) {
      window.open(url, '_blank')
    }
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  const isApkUploading = uploadedApkFile?.status === 'uploading'
  const isManualUploading = uploadedManualFile?.status === 'uploading'

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      requiredMark={(label, info) => (
        <>
          {label}
          {info.required && <span className="text-destructive ml-1">*</span>}
        </>
      )}
      className="space-y-6"
    >
      <Form.Item
        name="name"
        label="Tên phiên bản"
        rules={[{ required: true, message: 'Vui lòng nhập tên phiên bản' }]}
      >
        <Input
          placeholder="Nhập tên phiên bản"
          size="large"
          className={PILL_INPUT}
        />
      </Form.Item>

      <Form.Item
        name="apkFile"
        label="Cài đặt (APK)"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: 'Vui lòng tải lên tệp cài đặt' }]}
      >
        <Upload
          customRequest={(options) => handleCustomUpload(options, 'version')}
          maxCount={1}
          showUploadList={false}
          disabled={mode === 'update'}
          className="[&_.ant-upload-select]:!w-full"
        >
          <Button
            variant="outline"
            size="lg"
            disabled={isApkUploading || mode === 'update'}
            className="w-full flex items-center justify-between border-gray-300 overflow-hidden disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed"
          >
            <div className="flex items-center truncate">
              {uploadedApkFile ? (
                <span
                  className="truncate font-medium cursor-pointer"
                  // onClick={(e) => handlePreview(e, uploadedApkFile)}
                >
                  {uploadedApkFile.name}
                </span>
              ) : (
                <span className="text-gray-400 truncate">Tải lên tệp ...</span>
              )}
            </div>
            {isApkUploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <UploadIcon size={16} />
            )}
          </Button>
        </Upload>
      </Form.Item>

      <Form.Item
        name="manualFile"
        label="Hướng dẫn sử dụng"
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload
          customRequest={(options) => handleCustomUpload(options, 'version')}
          maxCount={1}
          showUploadList={false}
          className="[&_.ant-upload-select]:!w-full"
        >
          <Button
            variant="outline"
            size="lg"
            disabled={isManualUploading}
            className="w-full flex items-center justify-between border-gray-300 overflow-hidden"
          >
            <div className="flex items-center truncate">
              {uploadedManualFile ? (
                <span
                  className="truncate font-medium cursor-pointer"
                  // onClick={(e) => handlePreview(e, uploadedManualFile)}
                >
                  {uploadedManualFile.name}
                </span>
              ) : (
                <span className="text-gray-400 truncate">Tải lên tệp ...</span>
              )}
            </div>
            {isManualUploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <UploadIcon size={16} />
            )}
          </Button>
        </Upload>
      </Form.Item>

      <Form.Item 
        name="description" 
        label="Ghi chú"  
        rules={[{ required: true, message: 'Vui lòng nhập ghi chú' }]}
      >
        <Input.TextArea
          placeholder="Nhập ghi chú"
          size="large"
          autoSize={{ minRows: 4, maxRows: 6 }}
          className={TEXTAREA_INPUT}
        />
      </Form.Item>
    </Form>
  )
}
